import { getUnitValue, units } from '@ui/css';
import { TEMPLATE_CONTAINER_OFFSET_LEFT_PX } from '../offset-pages/_offset-pages.style.js';
import { getPhysicalPageContentHeightInPx } from '../offset-pages/util.calculate-physical-page-dimensions.js';
import { pageSelectors, lineCounter, content } from './_split-pages.style.js';

/**
 * collection of all elements that cannot have children. For these elements we
 * simply wish to insert them into the page and then move on to the next
 * element.
 */
const VOID_ELEMENTS = ["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"];
async function constructPhysicalPages(args) {
  await splitContentToPhysicalPages(args);
  if (args.lineCount?.showPerNthLine) {
    insertLineCount(args.destination, args.lineCount.showPerNthLine);
  }
  return args.destination.childElementCount;
}

/**
 * iterates over *all* nodes provided in the template source
 */
async function splitContentToPhysicalPages(args) {
  let currentPage = makeNewPhysicalPage(args.destination);
  const physicalPageHeightInPx = getUnitValue(units.vh(100), args.source);
  const physicalPageContentHeightInPx = getPhysicalPageContentHeightInPx({
    ...args,
    templatePage: args.source
  }, physicalPageHeightInPx, 0);
  const walker = document.createTreeWalker(args.source, NodeFilter.SHOW_ALL);

  // our nodeMappings keeps track of the relationship between nodes in our
  // source and the copied node on the *current* destination physical page.
  const nodeMappings = new WeakMap();
  nodeMappings.set(args.source, currentPage);
  let resumedAt = Date.now();

  //
  // STEP: Traverse through all nodes, keep track of relationships between
  // source nodes and nodes on the current destination page. For each word,
  // insert it into the current page and once overflows occurs, move on to the
  // next physical page, ensuring that parent/child structures are preserved on
  // all destination pages.
  //
  while (walker.nextNode()) {
    if (!walker.currentNode.parentNode) {
      throw new Error("splitContentToPhysicalPages(): Unable to resolve parent node for current element.");
    }
    if (walker.currentNode instanceof HTMLElement) {
      //
      // STEP: For HTML elements, simply insert them into the current page
      // (without their children, since we will copy these once encountered by
      // our walker) and keep track of the parent/child relationship.
      //
      if (VOID_ELEMENTS.includes(walker.currentNode.tagName.toLowerCase())) {
        const parent = nodeMappings.get(walker.currentNode.parentNode);
        parent?.appendChild(walker.currentNode.cloneNode(false));
      } else {
        const cloned = walker.currentNode.cloneNode(false);
        nodeMappings.set(walker.currentNode, cloned);
        const parent = nodeMappings.get(walker.currentNode.parentNode);
        parent?.appendChild(cloned);
      }
    } else {
      //
      // STEP: For text nodes we split content into individual words and insert
      // them into our destination...
      //
      const individualWords = walker.currentNode.textContent?.split(" ") ?? [];
      let parent = nodeMappings.get(walker.currentNode.parentNode);
      const appendedChildren = [];
      let appendToNextWord = [];
      for (let i = 0; i < individualWords.length; i++) {
        const word = individualWords[i];
        if (typeof word === "undefined") {
          continue;
        }
        if (word && !word.trim()) {
          appendToNextWord.push(word);
          continue;
        }
        if (word) {
          const child = document.createElement("span");
          child.dataset["word"] = "true";
          child.appendChild(
          // Note: we replace spaces with a non-breaking space to ensure that
          // the resulting node still has a height applied (matching the
          // current line-height), since line-count centering otherwise might
          // be placed incorrectly in relation to the current line. (the
          // first 'word' on a line may simply be a collection of white-space)
          document.createTextNode(`${appendToNextWord.join("")}${word.replaceAll(" ", "\u00A0")}`));
          appendToNextWord = [];
          appendedChildren.push(child);
          parent?.appendChild(child);
        }

        // Note: ensure we add spaces between words as they have been removed
        // during our .split(" ") call.
        if (i !== individualWords.length - 1) {
          const whitespace = document.createTextNode(" ");
          appendedChildren.push(whitespace);
          parent?.appendChild(whitespace);
        }
      }
      if (appendToNextWord.length) {
        const remainingWhitespace = document.createTextNode(appendToNextWord.join(""));
        appendedChildren.push(remainingWhitespace);
        parent?.appendChild(remainingWhitespace);
      }
      const hasOverflow = checkIfPhysicalPageHasOverflow(currentPage, physicalPageContentHeightInPx);

      //
      // STEP: ... in case this batch of inserted words caused overflow, then
      // we backtrack until the overflow has been removed and all the removed
      // words are then inserted into a new (next) physical page.
      //
      if (hasOverflow) {
        const overflowed = [];
        let latestChild = appendedChildren.pop();

        // remove words until we're no longer overflowing...
        do {
          if (latestChild) {
            overflowed.push(latestChild);
            parent?.removeChild(latestChild);
            latestChild = appendedChildren.pop();
          }
        } while (latestChild && checkIfPhysicalPageHasOverflow(currentPage, physicalPageContentHeightInPx));

        // ... construct a new page with all ancestors for the current node
        // also inserted, such that DOM-structure is preserved.
        currentPage = makeNewPhysicalPage(args.destination);
        nodeMappings.set(args.source, currentPage);
        const ancestors = resolveAncestors(walker.currentNode.parentNode, args.source);
        for (let i = 0; i < ancestors.length; i++) {
          const ancestorOnNewPage = nodeMappings.get(ancestors[i - 1] ?? args.source);
          const parentOnPreviousPage = ancestors[i];
          if (parentOnPreviousPage) {
            const parentOnNewPage = parentOnPreviousPage.cloneNode(false);
            ancestorOnNewPage?.appendChild(parentOnNewPage);

            // Note: update nodeMappings to reflect that we've moved on to a
            // new page.
            nodeMappings.set(parentOnPreviousPage, parentOnNewPage);
          }
        }
        parent = nodeMappings.get(walker.currentNode.parentNode);

        // ... and finally re-insert the overflowed words on the newly prepared
        // page.
        for (let i = overflowed.length - 1; i >= 0; i--) {
          const child = overflowed[i];
          if (!child) {
            continue;
          }
          parent?.appendChild(child);
        }
      }
    }

    //
    // Once we've performed our current unit of work then give the browser a
    // chance to perform other work before continuing.
    //
    if (Date.now() - resumedAt > 16) {
      await awaitIdleMoment();
      resumedAt = Date.now();
    }
  }
}

/**
 * iterates over all words constructed during our split to individual physical
 * pages and identifies the first word on each line. In case the line matches
 * the modulo, then an element is inserted into the dom, alongside the line,
 * specifying the count of the line.
 */
function insertLineCount(target, modulo) {
  const allWords = target.querySelectorAll(`${pageSelectors.includeInLineCount()} [data-word='true']`);
  const allWordsWithOffsets = Array.from(allWords).map(word => ({
    word,
    rect: word.getBoundingClientRect()
  }));
  let previousRect;
  let lineCount = 0;
  for (const {
    word,
    rect
  } of allWordsWithOffsets) {
    if (rect.height < 5) {
      continue;
    }
    const offset = rect.bottom;
    const previousOffset = previousRect?.bottom ?? 0;

    // In case the current word is significantly away from the previous word,
    // then we assume that we've moved on to the next line.
    if (Math.abs(offset - previousOffset) > 5) {
      lineCount++;
      word.dataset["lineCount"] = String(lineCount);
      if (lineCount % modulo === 0) {
        const nodeToInsertCounterInto = selectFirstNodeOnSameLine(word, offset);
        const counter = document.createElement("sup");
        counter.classList.add(lineCounter);
        counter.textContent = String(lineCount);

        // Specifies how many pixels the node needs to be shifted to be exactly
        // at the start of the parent word.
        // Note: as we insert our words into the template-container, than we
        // need to account for this offset to get the correct position.
        const offsetLeft = rect.left - word.offsetLeft + TEMPLATE_CONTAINER_OFFSET_LEFT_PX;

        // Note: we *cannot* use the properties "left" or "top" as this will
        // bring the counter outside of the text flow while also pulling the
        // related word out. This would result in a terrible selection
        // experience while also ruining text-to-speech capabilities in the
        // generated PDFs.
        counter.style.transform = `translate(calc(-100% - 12px + ${Math.round(offsetLeft)}px), calc(${Math.round(rect.height / 2)}px - 50%))`;
        nodeToInsertCounterInto.replaceChildren(counter, ...nodeToInsertCounterInto.childNodes);
      }
    }
    previousRect = rect;
  }
}
function checkIfPhysicalPageHasOverflow(templatePage, physicalPageContentHeightInPx) {
  return templatePage.clientHeight - physicalPageContentHeightInPx > 1;
}
function makeNewPhysicalPage(templateTarget) {
  const templateNode = document.createElement("div");
  templateNode.classList.add(content);
  templateTarget.appendChild(templateNode);
  return templateNode;
}
function awaitIdleMoment() {
  return new Promise(resolve => typeof requestIdleCallback === "undefined" ? setTimeout(resolve, 0) : requestIdleCallback(() => resolve()));
}
function resolveAncestors(node, root) {
  const base = [];
  let parent = node;
  while (parent && parent !== root) {
    base.push(parent);
    parent = parent.parentNode;
  }
  return base.reverse();
}
const ASSUMED_MINIMUM_LINE_HEIGHT = 15;
function selectFirstNodeOnSameLine(node, nodeOffset) {
  let candidate = node;
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
  walker.currentNode = node;
  do {
    // Note: Ensure we only select our text-nodes containing only text
    if (!(walker.currentNode instanceof Element) || walker.currentNode.childElementCount !== 0) {
      continue;
    }
    if (walker.currentNode.tagName.toLowerCase() === "br") {
      break;
    }
    if (Math.abs(walker.currentNode.getBoundingClientRect().bottom - nodeOffset) > ASSUMED_MINIMUM_LINE_HEIGHT) {
      break;
    }
    candidate = walker.currentNode;
  } while (walker.previousNode());
  return candidate;
}

export { constructPhysicalPages };
//# sourceMappingURL=util.construct-physical-pages.js.map
